import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * Admin > Customers tab: repeat customers, top 10 by spend/count,
 * retention rate, and a CSV export. Uses paid+confirmed orders only.
 */
export default function CustomerInsights() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics/customers").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const exportCsv = async () => {
    const API = process.env.REACT_APP_BACKEND_URL;
    const url = `${API}/api/admin/analytics/customers.csv`;
    window.open(url, "_blank");
  };

  if (!data) return <div className="mt-10 label-xs text-espresso-light">Loading…</div>;

  const stat = (label, value, testid) => (
    <div className="border border-line p-5 bg-ivory">
      <div className="label-xs text-espresso-light">{label}</div>
      <div className="mt-2 font-serif text-3xl text-espresso" data-testid={testid}>{value}</div>
    </div>
  );

  const table = (title, rows, columns, testid) => (
    <div className="mt-8" data-testid={testid}>
      <h3 className="font-serif text-2xl text-espresso mb-4">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-espresso-light">No customers yet.</p>
      ) : (
        <div className="overflow-x-auto border border-line">
          <table className="w-full text-sm">
            <thead className="bg-ivory-100 label-xs text-espresso-light">
              <tr>{columns.map((c) => <th key={c.k} className="text-left px-4 py-3">{c.label}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.email || i} className="border-t border-line">
                  {columns.map((c) => (
                    <td key={c.k} className="px-4 py-3 text-espresso">
                      {c.fmt ? c.fmt(r) : (r[c.k] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const cols = [
    { k: "email", label: "Email" },
    { k: "name", label: "Name", fmt: (r) => r.name || "—" },
    { k: "orders", label: "Orders" },
    { k: "total_spent", label: "Total spent", fmt: (r) => `₹${Math.round(r.total_spent || 0)}` },
    { k: "avg_gap_days", label: "Avg gap (days)", fmt: (r) => r.avg_gap_days ?? "—" },
    { k: "last_at", label: "Last order", fmt: (r) => (r.last_at || "").slice(0, 10) },
  ];

  return (
    <div className="mt-8" data-testid="customer-insights-panel">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <h2 className="font-serif text-3xl text-espresso">Customer insights</h2>
        <Button onClick={exportCsv} data-testid="customers-csv-export" className="bg-espresso hover:bg-espresso-hover text-ivory">
          Export CSV
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        {stat("Total customers", data.total_customers, "insights-total-customers")}
        {stat("Repeat customers", data.repeat_customers.length, "insights-repeat-count")}
        {stat("Retention rate", `${data.retention_rate}%`, "insights-retention-rate")}
        {stat("Top spender", data.top_by_spend[0] ? `₹${Math.round(data.top_by_spend[0].total_spent)}` : "—", "insights-top-spender")}
      </div>

      {table("Top 10 by spend", data.top_by_spend, cols, "top-by-spend-table")}
      {table("Top 10 by order count", data.top_by_count, cols, "top-by-count-table")}
      {table("Repeat customers", data.repeat_customers, cols, "repeat-customers-table")}
    </div>
  );
}
